import { scale, type AttachmentInput, type MaskRect } from '@innspel/core';
import type { Fangst } from './typer.ts';

/** JPEG at this quality keeps a 2× viewport well under 5 MB (K9) and makes the masks irreversible in practice. */
const KVALITET = 0.85;
const TAK = 4096;

export interface Masking {
  /** Private fields — cannot be removed (DD-39). */
  automasker: MaskRect[];
  /** The submitter's own: «Masker felt» (mask a field) and «Tegn over» (draw over) (DD-40). */
  egne: MaskRect[];
  /** «Masker all tekst» (mask all text) (DD-40). */
  allTekst: boolean;
}

/**
 * The composition is what gets uploaded (K4): the bitmap with all the masks drawn
 * INTO the pixels, re-encoded as JPEG. The raw bitmap is used only for the
 * preview and never leaves the widget.
 *
 * The colours are `screenRules` from the theme (theme/theme.ts) — the one place
 * the palette does not apply, because the masks must be visible whatever the image
 * contains. They are read from the CSS variables so the number lives in one place.
 */
export async function compose(fangst: Fangst, maskering: Masking, farger = lesMaskefarger()): Promise<AttachmentInput> {
  // Alt innenfor 4096² (K9). Maskene skaleres med — de er i fangstens piksler.
  const s = Math.min(1, TAK / Math.max(fangst.width, fangst.height, 1));
  const ut = { width: Math.max(1, Math.round(fangst.width * s)), height: Math.max(1, Math.round(fangst.height * s)) };
  const inn = { width: fangst.width, height: fangst.height };

  const canvas = document.createElement('canvas');
  canvas.width = ut.width;
  canvas.height = ut.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas is not available');

  ctx.drawImage(fangst.bilde, 0, 0, ut.width, ut.height);

  if (maskering.allTekst) {
    if (fangst.tekstRekter && fangst.tekstRekter.length) {
      ctx.fillStyle = farger.auto;
      for (const r of fangst.tekstRekter) fyll(ctx, scale(r, inn, ut));
    } else {
      // En opplastet fil har ingen tekstgeometri: hele bildet pikseleres, så
      // tekst blir uleselig mens layouten er synlig. Nedskalering er universell
      // — `ctx.filter` finnes ikke overalt.
      pikseler(ctx, canvas);
    }
  }

  ctx.fillStyle = farger.auto;
  for (const r of maskering.automasker) fyll(ctx, scale(r, inn, ut));
  ctx.fillStyle = farger.egen;
  for (const r of maskering.egne) fyll(ctx, scale(r, inn, ut));

  const body = await tilBlob(canvas);
  return { mime: 'image/jpeg', body, width: ut.width, height: ut.height };
}

function fyll(ctx: CanvasRenderingContext2D, r: MaskRect): void {
  ctx.fillRect(r.x, r.y, r.width, r.height);
}

function pikseler(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): void {
  const faktor = 24;
  const liten = document.createElement('canvas');
  liten.width = Math.max(1, Math.round(canvas.width / faktor));
  liten.height = Math.max(1, Math.round(canvas.height / faktor));
  const lctx = liten.getContext('2d');
  if (!lctx) return;
  lctx.imageSmoothingEnabled = true;
  lctx.drawImage(canvas, 0, 0, liten.width, liten.height);
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(liten, 0, 0, liten.width, liten.height, 0, 0, canvas.width, canvas.height);
}

function tilBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('could not encode the image'))), 'image/jpeg', KVALITET);
  });
}

/** The mask colours from tokens.css. The fallback is the same numbers as in theme.ts, for a document without the stylesheet. */
export function lesMaskefarger(): { auto: string; egen: string } {
  const les = (navn: string, reserve: string): string => {
    if (typeof getComputedStyle !== 'function') return reserve;
    const v = getComputedStyle(document.documentElement).getPropertyValue(navn).trim();
    return v || reserve;
  };
  return {
    auto: les('--innspel-screen-auto-mask-fill', '#101114'),
    egen: les('--innspel-screen-brush-fill', '#E5007D'),
  };
}
